import React from 'react';
import {
  TariffExclusionsTable,
  TariffResultsTable,
  TariffFtaTable,
} from './TarrifTypes';
import { ImportBan, importBanResultRow } from 'utils/importBans';

interface ITariffProps {
  tariffResults: any;
  productValue: number;
  materialPct?: number;
  dutyCost?: number;
  totalCost?: number;
  baseCost?: number;
  displayAdditionalRate?: string;
  tariffCandidates?: any[];
  enabledExclusions?: Record<string, boolean>;
  toggleExclusion?: (codeDigits: string, enabled: boolean) => void;
  ftaOptions?: FtaOption[];
  enabledFtas?: Record<string, boolean>;
  toggleFta?: (code: string, enabled: boolean) => void;
  explanationText: string;
  aiLoading: boolean;
  mpf?: number;
  hmf?: number;
}

type FtaOption = { code: string; name: string };

const usd = (n: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(
    Number(n || 0),
  );

const DutyComp: React.FC<ITariffProps> = ({
  tariffResults,
  tariffCandidates = [],
  enabledExclusions = {},
  toggleExclusion,
  ftaOptions: ftaOptionsProp,
  enabledFtas,
  toggleFta,
}) => {
  const safeTariffResults =
    tariffResults && typeof tariffResults === 'object' ? tariffResults : {};
  const isSnapshot = Boolean(
    safeTariffResults.inputs &&
      safeTariffResults.base &&
      safeTariffResults.results,
  );
  const importBan: ImportBan | null = isSnapshot
    ? safeTariffResults.results?.activeImportBan ?? null
    : safeTariffResults.activeImportBan ?? null;

  // per-line data
  const linesRaw: any[] = isSnapshot
    ? Array.isArray(safeTariffResults.results?.lines)
      ? safeTariffResults.results.lines
      : []
    : Array.isArray(safeTariffResults.lines)
    ? safeTariffResults.lines
    : [];

  const lines = Array.isArray(linesRaw) ? linesRaw : [];
  const hasSplit = lines.length > 0;

  // rows shown in "Duty Summary"
  const summaryRows: any[] = isSnapshot
    ? Array.isArray(safeTariffResults.results?.summaryRows)
      ? safeTariffResults.results.summaryRows
      : []
    : Array.isArray(safeTariffResults.results)
    ? safeTariffResults.results
    : [];

  // FTAs & exclusions pools (prefer props when present)
  const ftaOptions: FtaOption[] =
    ftaOptionsProp ??
    (isSnapshot
      ? (safeTariffResults.results?.ftaOptions as FtaOption[]) ?? []
      : []);

  const candidatesForExclusions: any[] = tariffCandidates.length
    ? tariffCandidates
    : isSnapshot
    ? safeTariffResults.results?.optionalExclusions ?? []
    : safeTariffResults.optionalExclusions ?? [];

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {/* LEFT */}
        <div className='tariff-container-left'>
          {importBan ? (
            <div style={{ width: '100%' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginBottom: '5px' }}
              >
                Duty Summary
              </div>
              <div
                className='screening-badge chip-red lens-chip'
                style={{
                  display: 'block',
                  width: '100%',
                  margin: '0 0 12px 0',
                  padding: '10px 14px',
                  lineHeight: 1.45,
                }}
              >
                {importBan.narrative}. These goods can&apos;t be entered, so no
                duty applies.
              </div>
              <TariffResultsTable tariffResults={[importBanResultRow(importBan)]} />
              {importBan.description ? (
                <div className='tariff-desc-text' style={{ marginTop: '8px' }}>
                  {importBan.description}
                </div>
              ) : null}
            </div>
          ) : !hasSplit ? (
            <div style={{ width: '100%' }}>
              <TariffResultsTable tariffResults={summaryRows} />
            </div>
          ) : (
            <div style={{ width: '100%' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginBottom: '5px' }}
              >
                Duty Summary • Line Breakdown
              </div>

              {lines.map((line: any, idx: number) => {
                const lineValueDisplay = Number(line?.value ?? 0);
                // The engine's own per-line duty. A ticket saved before the
                // engine reported one has none to show.
                const lineDuty =
                  typeof line?.duty === 'number' ? usd(line.duty) : '—';

                return (
                  <div
                    key={line?.id || idx}
                    style={{ marginTop: idx === 0 ? 12 : 18 }}
                  >
                    <div className='screening-ai-label-text'>
                      {line?.label || `Line ${idx + 1}`}
                      <span style={{ float: 'right' }}>
                        Value: <strong>{usd(lineValueDisplay)}</strong> • Duty:{' '}
                        <strong>{lineDuty}</strong>
                      </span>
                    </div>
                    <TariffResultsTable tariffResults={line?.rows} />
                  </div>
                );
              })}
            </div>
          )}

          {/* Free Trade Agreements */}
          {!importBan && Array.isArray(ftaOptions) && ftaOptions.length > 0 && (
            <div style={{ width: '100%', marginTop: '18px' }}>
              <div
                className='screening-ai-label-text'
                style={{ marginBottom: '5px' }}
              >
                Free Trade Agreements
              </div>
              <TariffFtaTable
                options={ftaOptions}
                enabled={enabledFtas}
                onToggle={toggleFta} // omit/undefined on tickets => read-only
              />
            </div>
          )}

          {/* Optional Exclusions */}
          {!importBan && <div style={{ width: '100%' }}>
            <div
              className='screening-ai-label-text'
              style={{ marginBottom: '5px' }}
            >
              Potential Exclusions
            </div>
            <TariffExclusionsTable
              candidates={candidatesForExclusions}
              enabled={enabledExclusions || {}}
              onToggle={(code, enabled) => toggleExclusion?.(code, enabled)}
            />
          </div>}
        </div>
      </div>
    </>
  );
};

export default DutyComp;
